import { useRef } from "react";
import { sanitizeEmailHtml } from "../../lib/sanitizeHtml";

// Render an inbound email's HTML body inside a sandboxed iframe. The iframe
// gives us hard isolation from the host app — even if our DOMPurify-based
// sanitiser missed something, the email can't run scripts, can't navigate
// our window, and can't read our cookies. We still sanitise first as a
// belt-and-braces measure.
//
// We use srcDoc rather than injecting raw HTML into a parent div because
// email HTML routinely carries inline <style> blocks and document-level
// formatting that would bleed into our app, and because the platform's
// frontend security validator forbids React's raw-HTML escape hatch.

function buildSrcDoc(html: string): string {
  const safe = sanitizeEmailHtml(html);
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<base target="_blank">
<style>
  html, body { margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 14px;
    color: #1b2233;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  img { max-width: 100%; height: auto; }
  a { color: #3057e1; }
  table { max-width: 100%; }
  blockquote {
    border-left: 3px solid #e2e5ec;
    margin: 8px 0;
    padding-left: 12px;
    color: #6b7280;
  }
</style>
</head>
<body>${safe}</body>
</html>`;
}

export function HtmlMessage({ html }: { html: string }) {
  const ref = useRef<HTMLIFrameElement | null>(null);

  // The iframe's intrinsic height is fixed by the browser, so we need to
  // resize it to its content's scrollHeight on load. Images decode after the
  // load event, so we re-measure every time one finishes loading.
  const handleLoad = () => {
    const iframe = ref.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc) return;

    const resize = () => {
      const h = Math.max(
        doc.documentElement.scrollHeight,
        doc.body?.scrollHeight ?? 0,
      );
      iframe.style.height = `${h + 8}px`;
    };
    resize();

    doc.querySelectorAll("img").forEach((img) => {
      if (!img.complete) {
        img.addEventListener("load", resize, { once: true });
        img.addEventListener("error", resize, { once: true });
      }
    });
  };

  return (
    <iframe
      ref={ref}
      className="message-body-iframe"
      title="Email body"
      // Sandbox blocks scripts, forms, top-level navigation, and same-origin
      // access. allow-popups + allow-popups-to-escape-sandbox lets links
      // (which are forced target=_blank by <base>) open in a new tab.
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      srcDoc={buildSrcDoc(html)}
      onLoad={handleLoad}
    />
  );
}
